import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '../../../common/components/atoms/Button';
import TextArea from '../../../common/components/atoms/TextArea';
import { RANGE_TIME_FORMAT } from '../utils/annotationRange';
import { NOTE_TEXT_MAX_LENGTH, NOTE_TEXT_MIN_LENGTH } from '../utils/noteConstants';

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(56, 56, 56, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 6px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
  padding: 20px;
  width: 420px;
  max-width: 90vw;
`;

const Title = styled.h3`
  margin: 0 0 4px 0;
  font-size: 16px;
  color: ${props => props.theme.colors.greyXXD};
`;

const TimeInfo = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.greyXD};
  margin-bottom: 12px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

function NoteCreationModal(props) {
  const { t } = useTranslation();
  const { startTime, endTime, saving, onSave, onClose } = props;
  const [noteText, setNoteText] = useState('');

  const isRange = !!endTime && endTime !== startTime;
  const trimmed = noteText.trim();
  // Mirror the backend note_text bounds so we validate before submitting.
  const tooLong = noteText.length > NOTE_TEXT_MAX_LENGTH;
  const isValid = trimmed.length >= NOTE_TEXT_MIN_LENGTH && !tooLong;
  const canSave = isValid && !saving;

  const cardRef = useRef(null);

  const handleSave = useCallback(() => {
    if (isValid) onSave(trimmed);
  }, [isValid, trimmed, onSave]);

  // Modal a11y: close on Escape, trap Tab focus within the dialog, and restore
  // focus to the previously focused element when the dialog closes.
  useEffect(() => {
    const previouslyFocused = document.activeElement;

    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const focusable = Array.prototype.slice.call(
        cardRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Only restore focus if the origin element is still in the DOM; a detached
      // node's .focus() is a silent no-op (focus would fall to <body>).
      if (previouslyFocused && document.contains(previouslyFocused) && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [onClose]);

  return (
    <Backdrop onMouseDown={onClose}>
      <Card
        innerRef={(node) => { cardRef.current = node; }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="note-creation-modal-title"
        onMouseDown={e => e.stopPropagation()}
      >
        <Title id="note-creation-modal-title">
          {isRange
            ? t('annotations.create.titleRange', { defaultValue: 'Add time-range note' })
            : t('annotations.create.titlePoint', { defaultValue: 'Add note' })}
        </Title>
        <TimeInfo>
          {isRange
            ? `${moment(startTime).format(RANGE_TIME_FORMAT)} → ${moment(endTime).format(RANGE_TIME_FORMAT)}`
            : moment(startTime).format(RANGE_TIME_FORMAT)}
        </TimeInfo>
        <TextArea
          autoFocus
          height="120px"
          width="100%"
          value={noteText}
          maxLength={NOTE_TEXT_MAX_LENGTH}
          aria-label={t('annotations.create.placeholder', { defaultValue: 'Write a note…' })}
          placeholder={t('annotations.create.placeholder', { defaultValue: 'Write a note…' })}
          onChange={e => setNoteText(e.target.value)}
        />
        {tooLong && (
          <TimeInfo role="alert">
            {t('annotations.create.tooLong', {
              defaultValue: 'Note must be {{max}} characters or fewer.',
              max: NOTE_TEXT_MAX_LENGTH
            })}
          </TimeInfo>
        )}
        <Actions>
          <Button cancel type="button" onClick={onClose} fontSize="12px">
            {t('annotations.create.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave} fontSize="12px">
            {t('annotations.create.save', { defaultValue: 'Save' })}
          </Button>
        </Actions>
      </Card>
    </Backdrop>
  );
}

NoteCreationModal.propTypes = {
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

NoteCreationModal.defaultProps = {
  endTime: null,
  saving: false
};

export default NoteCreationModal;
